import React, {FC, useCallback} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {pluralise} from '@optimaxdev/utils';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {FeatureWithAnalytics} from 'components/feature';
import {CART_UPGRADE_NAVIGATION} from 'constants/analytics/events/cartUpgrade';
import {ApplicationStoreType} from 'desktop/store';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {useHostDispatch} from 'hooks/hostHooks/hostHooks';
import {flags} from 'libs/rollout';

import {getCartItemsCount} from '../../store/selectors';

import {ShareCartTooltipButton} from '../shareCartTooltipButton/shareCartTooltipButton';

import s from './cartTitle.scss';

export type PropsType = {
    count: number;
    isIndent: boolean;
};

/**
 * Displays title of react cart
 * @params {PropsType} props - component props
 * @returns {ReactElement} component for cart title
 */
export const CartTitle: FC<PropsType> = ({count, isIndent}) => {
    const dispatch = useHostDispatch();
    const {is_logged_in: isLoggedIn} = useGetCustomerData();
    const isUnauthorized = !isLoggedIn;

    /**
     * Open authorization popup
     */
    const authorizeHandler = useCallback(() => {
        dispatch({
            payload: {
                tab: 'login',
                visible: true,
            },
            type: 'AUTH_POPUP@SET_VISIBLE',
        });
        sendDesktopAnalyticsEvent(CART_UPGRADE_NAVIGATION, 'Save Cart');
    }, [dispatch]);

    /**
     * Sending analytics after clicked "Continue Shopping"
     */
    const onSendAnalytics = useCallback(() => {
        sendDesktopAnalyticsEvent(CART_UPGRADE_NAVIGATION, 'Continue Shopping');
    }, []);

    const isDCartShareEnabled = flags.dCartShare.isEnabled();

    return (
        <div
            className={classNames(s.wrapper, {
                [s.indent]: isIndent,
            })}
        >
            <div
                className={classNames(s.infoBlock, {
                    [s.shareCartEnabled]: isDCartShareEnabled,
                })}
            >
                <h2
                    className={classNames(s.title, {
                        [s.shareCartEnabled]: isDCartShareEnabled,
                    })}
                >
                    Shopping Cart
                </h2>
                <FeatureWithAnalytics
                    fallback={
                        <span className={s.itemsCount}>
                            <span data-test-name="cartItemsCount">
                                {count} {pluralise('item', count)}
                            </span>{' '}
                            in cart
                        </span>
                    }
                    flag="dCartShare"
                    isInteraction={false}
                    platform="desktop"
                >
                    <ShareCartTooltipButton count={count} />
                </FeatureWithAnalytics>
                <p
                    className={classNames(s.links, {
                        [s.shareCartEnabled]: isDCartShareEnabled,
                    })}
                >
                    <a
                        className={classNames(s.link, 'continue')}
                        href="/"
                        onClick={onSendAnalytics}
                    >
                        Continue Shopping
                    </a>
                    {isUnauthorized && (
                        <>
                            {' '}
                            or{' '}
                            <button
                                className={classNames(s.link, 'btn_save')}
                                data-test-name="saveCart"
                                onClick={authorizeHandler}
                                type="button"
                            >
                                Save Cart
                            </button>
                        </>
                    )}
                </p>
            </div>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    count: getCartItemsCount(store),
});

export const CartTitleConnected = connect(mapStateToProps)(CartTitle);
